<script>
import { mapGetters } from 'vuex';

import AsyncButton from '@shell/components/AsyncButton';
import ResourceCancelModal from '@shell/components/ResourceCancelModal';
import { _VIEW } from '@shell/config/query-params';

export default {
  emits: ['cancel-confirmed', 'finish'],

  components: { AsyncButton, ResourceCancelModal },
  props:      {
    mode: {
      type:    String,
      default: 'create',
    },

    isForm: {
      type:    Boolean,
      default: true,
    },

    // Override the set of labels shown on the button from the default save/create.
    finishButtonMode: {
      type:    String,
      default: null,
    },

    confirmCancelRequired: {
      type:    Boolean,
      default: false,
    },

    confirmBackRequired: {
      type:    Boolean,
      default: true,
    },

    showCancel: {
      type:    Boolean,
      default: true
    },

    /**
     * Inherited global identifier prefix for tests
     * Define a term based on the parent component to avoid conflicts on multiple components
     */
    componentTestid: {
      type:    String,
      default: 'form-footer'
    }
  },

  data() {
    return { isCancelModal: false };
  },

  computed: {
    ...mapGetters({ t: 'i18n/t' }),

    isView() {
      return this.mode === _VIEW;
    },
  },

  methods: {
    checkCancel(isCancel) {
      if (isCancel) {
        this.isCancelModal = true;
      } else {
        this.isCancelModal = false;
      }
      this.$refs.cancelModal.show();
    },

    confirmCancel(isCancel) {
      this.$emit('cancel-confirmed', isCancel);
    },
  },
};
</script>

<template>
  <div class="cru-resource-footer">
    <slot name="footer-prefix" />
    <slot name="cancel">
      <button
        v-if="!isView && showCancel"
        id="cru-cancel"
        :data-testid="componentTestid + '-cancel'"
        type="button"
        class="btn role-secondary"
        @click="confirmCancelRequired ? checkCancel(true) : $emit('cancel-confirmed', true)"
      >
        <t k="generic.cancel" />
      </button>
    </slot>
    <slot :checkCancel="checkCancel">
      <AsyncButton
        v-if="!isView"
        :data-testid="componentTestid + '-create'"
        :mode="finishButtonMode || mode"
        @click="$emit('finish', $event)"
      />
    </slot>
    <ResourceCancelModal
      ref="cancelModal"
      :is-cancel-modal="isCancelModal"
      :is-form="isForm"
      @confirm-cancel="confirmCancel($event)"
    />
  </div>
</template>

<style lang="scss" scoped>
.cru-resource-footer {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 24px;
  padding-top: 18px;
  border-top: 1px solid var(--border, var(--header-border));
  z-index: z-index('cruFooter');

  :deep(.btn) {
    margin-left: 0;
    min-height: 36px;
    line-height: 1.4;
    font-size: 13px;
  }

  :deep(.controls-steps), :deep(.controls-middle), :deep(.controls-right) {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
  }

  @media (max-width: 600px) {
    gap: 8px;
    :deep(.controls-steps), :deep(.controls-middle), :deep(.controls-right) { gap: 8px; }
    :deep(.btn) { min-height: 40px; }
  }
}
</style>
